import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";

import dayjs from "dayjs";
import format from "date-fns/format";
import getDay from "date-fns/getDay";
import th from "date-fns/locale/th";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";

import {
  Box,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircleIcon from "@mui/icons-material/Circle";
import FileUploadIcon from '@mui/icons-material/FileUpload';

import ButtonBlue from "../general/ButtonBlue";

const FormControlStyled = styled(FormControl)((disabled) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "#919eab14",
    height: 56,
    padding: "0px 12px",
    borderRadius: 8,
    "&.Mui-focused": {
      backgroundColor: "#919eab14",
    },
    "& .MuiSelect-select": {
      padding: "4px 0 5px 8px",
    },
    "&:hover": {
      ...(disabled ? {} : { backgroundColor: "#919eab29" }),
      "&:before": {
        border: "none !important",
      },
    },
    "&::after": {
      border: "none",
    },
    "&::before": {
      border: "none",
      borderBottomStyle: "none !important",
    },
  },
}));

const StyledCalendar = styled(Calendar)({
  height: "350px !important",
  "& .rbc-date-cell": {
    paddingRight: 0,
    textAlign: "center",
  },
  "& .rbc-month-row": {
    justifyContent: "center",
  },
  "& .rbc-event-content": {
    display: "none !important",
  },
  "& .rbc-row-segment": {
    //]]display: "none !important",
  },
  "& .rbc-today": {
    borderRadius: 4,
  },
});

const StyledToolbarCalendar = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 16,
  "& .todayBtn": {
    borderColor: "#007afe",
    color: "#ffffff",
    backgroundColor: "#007afe",
  },
  "& .prevNextBtn": {
    marginRight: 4,
    marginLeft: 4,
  },
  "& .toolbarCalendarLabel": {
    fontSize: 24,
  },
});

const locales = {
  "th-TH": th,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const StyledEmpty = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 350,
});

const CalendarAndListHoliday = ({ toggleDrawerUpload, handleOpenDrawerHoliday, isReadOnly }) => {
  const today = dayjs().toDate();
  const { t, i18n } = useTranslation();
  const { result: holidayStore } = useSelector((state) => state.holiday);
  const [listHolidayPattern, setListHolidayPattern] = useState([]);
  const [holidayPatternSelected, setHolidayPatternSelected] = useState("");
  const [activeMonth, setActiveMonth] = useState(dayjs(today).get("month"));
  const [activeYear, setActiveYear] = useState(dayjs(today).get("year"));
  const [listHoliday, setListHoliday] = useState([]);

  useEffect(() => {
    if (holidayStore) {
      fetchData();
    }
  }, [holidayStore, activeYear]);

  useEffect(() => {
    if(holidayStore && holidayPatternSelected && holidayPatternSelected !== ""){
      let filterListHolidays = holidayStore[activeYear].find((pattern) => (pattern.idHolidayPattern === holidayPatternSelected));
      if(filterListHolidays && filterListHolidays.listHoliday && filterListHolidays.listHoliday.length > 0){
        setListHoliday(filterListHolidays.listHoliday);
      }else{
        setListHoliday([]);
      }
    }
  },[holidayPatternSelected]);

  const fetchData = () => {
    if(Object.keys(holidayStore).length > 0 && activeYear){
      let listHolidayPatternYear = holidayStore[activeYear];
      if(listHolidayPatternYear && listHolidayPatternYear.length > 0){
        setListHolidayPattern(listHolidayPatternYear);
        setHolidayPatternSelected(listHolidayPatternYear[0].idHolidayPattern);
        setListHoliday(listHolidayPatternYear[0].listHoliday);
      }else{
        setListHolidayPattern([]);
        setHolidayPatternSelected("");
        setListHoliday([]);
      }
    }else{
      setListHolidayPattern([]);
      setHolidayPatternSelected("");
      setListHoliday([]);
    }
  };

  const CustomToolbar = (toolbar) => {
    const goToBack = async () => {
      toolbar.date.setMonth(toolbar.date.getMonth() - 1);
      toolbar.onNavigate("prev");
      setActiveMonth(toolbar.date.getMonth());
      setActiveYear(toolbar.date.getFullYear());
    };

    const goToNext = async () => {
      toolbar.date.setMonth(toolbar.date.getMonth() + 1);
      toolbar.onNavigate("next");
      setActiveMonth(toolbar.date.getMonth());
      setActiveYear(toolbar.date.getFullYear());
    };

    const goToCurrent = async () => {
      const now = new Date();
      toolbar.date.setMonth(now.getMonth());
      toolbar.date.setYear(now.getFullYear());
      toolbar.onNavigate("current");
      setActiveMonth(toolbar.date.getMonth());
      setActiveYear(toolbar.date.getFullYear());
    };

    const label = () => {
      const date = dayjs(toolbar.date);
      return (
        <span style={{ fontSize: 18, margin: 8 }}>
          <b>{date.format("MMMM")}</b>
          <span> {date.format(i18n.language === "th" ? "BBBB" : "YYYY")}</span>
        </span>
      );
    };

    return (
      <StyledToolbarCalendar>
        <label className={"toolbarCalendarLabel"}>{label()}</label>
        <div>
          <IconButton
            aria-label="prev"
            size="small"
            className={`prevNextBtn`}
            onClick={goToBack}
            disabled={
              !dayjs()
                .date(1)
                .month(activeMonth)
                .year(activeYear)
                .isAfter(
                  dayjs(today).date(1).month(0).subtract(1, "year"),
                  "day"
                )
            }
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <ButtonBlue
            size={"small"}
            variant={"contained"}
            onClick={goToCurrent}
          >
            {t("Today")}
          </ButtonBlue>
          <IconButton
            aria-label="next"
            size="small"
            className={`prevNextBtn`}
            onClick={goToNext}
            disabled={
              !dayjs()
                .date(1)
                .month(activeMonth)
                .year(activeYear)
                .isBefore(dayjs(today).date(31).month(11).add(1, "year"), "day")
            }
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
      </StyledToolbarCalendar>
    );
  };

  const eventPropGetter = (event) => {
    let newStyle = {
      color: "black",
      borderRadius: 4,
      border: "none",
    };
    if (event.resource) {
      return {
        className: event.resource.typeEvent + "Event",
        style: newStyle,
      };
    } else {
      return {
        className: "",
        style: newStyle,
      };
    }
  };

  const RenderHolidayList = () => {
    if (listHoliday) {
      var holidayMonth = listHoliday.filter(
        (value) =>
          dayjs(value.dateHoliday).get("month") === activeMonth &&
          dayjs(value.dateHoliday).get("year") === activeYear
      );
      if (holidayMonth.length > 0) {
        return holidayMonth.map((value) => (
          <div key={value.idHoliday} style={{ marginBottom: 8 }}>
            <Typography variant="h6" style={{ fontSize: 18 }}>
              <CircleIcon
                style={{
                  fontSize: 12,
                  marginRight: 4,
                  color: "#fc4b6c",
                }}
              />
              {i18n.language === "th" ? 
                value.name 
                : (value.name_EN || value.name || "-")
              }
            </Typography>
            <Typography
              color="text.secondary"
              style={{
                paddingLeft: 12,
                fontSize: 16,
                fontWeight: 500,
              }}
            >
              {dayjs(value.dateHoliday).format(
                `DD MMM ${i18n.language === "th" ? "BBBB" : "YYYY"}`
              )}
            </Typography>
          </div>
        ));
      } else {
        return (
          <StyledEmpty className="empty-holiday">
            <Typography color="text.secondary" align="center">
              {t("NoHoliday")}
            </Typography>
          </StyledEmpty>
        );
      }
    }
  };

  return (
    <Fragment>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Grid item>
          <Typography variant="h6">{t("Calendar")}</Typography>
        </Grid>
        <Grid item>
          <Box display="flex" alignItems="center">
            <FormControlStyled variant="filled">
              <Select
                style={{
                  width: "200px",
                }}
                displayEmpty
                value={holidayPatternSelected}
                onChange={(e) => {
                  setHolidayPatternSelected(e.target.value);
                }}
                disabled={listHolidayPattern.length <= 0 ? true : false}
              >
                {listHolidayPattern.length > 0 ? (
                  listHolidayPattern.map((hp) => (
                    <MenuItem key={hp.idHolidayPattern} value={hp.idHolidayPattern}>
                      {hp.holidayPatternName}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value={""}>{t("HolidayPatternNotFound")}</MenuItem>
                )}
              </Select>
            </FormControlStyled>
            {isReadOnly ? 
            (<Fragment></Fragment>) : (
              <Box display="flex" alignItems="center" marginLeft="16px">
              <ButtonBlue
                variant={"contained"}
                startIcon={<FileUploadIcon />}
                style={{ marginRight: 8 }}
                onClick={toggleDrawerUpload(true)}
              >
                {t("UploadHoliday")}
              </ButtonBlue>
              <ButtonBlue
                variant={"contained"}
                startIcon={<AddIcon />}
                onClick={() => {
                  handleOpenDrawerHoliday({
                    type: "Add",
                    holiday: null
                  });
                }}
              >
                {t("addHoliday")}
              </ButtonBlue>
              </Box>
            )
            }
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: "24px" }}>
        <Grid item xs={6}>
          <StyledCalendar
            className="calendar-leave"
            localizer={localizer}
            events={listHoliday}
            views={["month"]}
            startAccessor="start"
            endAccessor="end"
            popup={true}
            defaultDate={new Date()}
            eventPropGetter={eventPropGetter}
            components={{
              toolbar: CustomToolbar,
            }}
            formats={{
              dateFormat: "d",
              dayHeaderFormat: "d MMMM yyyy",
              weekdayFormat: "E",
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <div>{RenderHolidayList()}</div>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default CalendarAndListHoliday;
